import type { ActivityEventDto, ActivityVerb } from '@familyhub/shared';

/**
 * The separator the recepten importer glues a measure and the recipe name with
 * when it bulk-adds ingredients ("6 el — Mosterd-bloemkoolsoep met kaas", see
 * `recepten/AddToListSheet.tsx`). The measure never contains it, so the first
 * occurrence is always the measure/source boundary.
 */
const NOTE_SEP = ' — ';

/**
 * Clustering the flat event stream into the cards the feed draws.
 *
 * The server returns events newest-first; a card is a run of them by the **same
 * person, same verb, same day**, and close together in time. That last clause is
 * what puts two separate "added" bursts on one day into two cards rather than
 * one — someone adding bread in the morning and wine at night did two things,
 * and a feed that merged them would erase that.
 *
 * Grouping lives on the client because it is presentation, exactly like
 * `groupList`: the server decides *what* happened, the screen decides how it
 * reads. Keeping it a pure function is what lets the day-boundary and gap rules
 * be tested without a browser.
 */

/** A run of same-actor, same-verb events shown as one card. */
export interface ActivityCard {
  /** Stable React key: the newest event's key, which is unique. */
  key: string;
  verb: ActivityVerb;
  actor: { id: string; display_name: string };
  /** The newest event in the run — the date the card header shows. */
  at: string;
  events: ActivityEventDto[];
}

/**
 * Events within this of each other join one card; a wider gap starts a new one.
 * Three hours is "one sitting" — long enough that a shop planned across a couple
 * of interruptions stays one card, short enough that morning and evening split.
 */
const CLUSTER_GAP_MS = 3 * 60 * 60 * 1000;

/** Local calendar day, so "same day" means the household's midnight, not UTC's. */
function localDay(iso: string): string {
  return new Date(iso).toDateString();
}

export function groupActivity(events: ActivityEventDto[]): ActivityCard[] {
  const cards: ActivityCard[] = [];

  for (const event of events) {
    const card = cards[cards.length - 1];
    const last = card?.events[card.events.length - 1];
    // `last` is the oldest event in the open card (events arrive newest-first),
    // so the gap to this still-older event is `last.at - event.at`.
    const joins =
      card !== undefined &&
      last !== undefined &&
      card.verb === event.verb &&
      card.actor.id === event.actor.id &&
      localDay(last.at) === localDay(event.at) &&
      Date.parse(last.at) - Date.parse(event.at) <= CLUSTER_GAP_MS;

    if (joins) {
      card!.events.push(event);
    } else {
      cards.push({
        key: event.event_key,
        verb: event.verb,
        actor: event.actor,
        at: event.at,
        events: [event],
      });
    }
  }

  return cards;
}

/** One row of a card: its event, plus the dim second line to draw under the name. */
export interface DescribedRow {
  event: ActivityEventDto;
  /**
   * The secondary line. A bare measure ("6 el") once the recipe name has been
   * hoisted to the card; otherwise the note as written; `null` for no line.
   */
  meta: string | null;
}

function splitNote(note: string): { measure: string; source: string; hadSep: boolean } {
  const i = note.indexOf(NOTE_SEP);
  if (i === -1) return { measure: '', source: note, hadSep: false };
  return { measure: note.slice(0, i), source: note.slice(i + NOTE_SEP.length), hadSep: true };
}

/**
 * Presentation of a card's rows — the same layer as `groupActivity`, deciding
 * *how* the facts read.
 *
 * A recipe's ingredients are bulk-added with the recipe name welded onto every
 * note, so a seven-ingredient soup prints the same long string seven times and
 * crowds the ingredient name off the row. When a run shares one source — and at
 * least one row named it with a measure, so two identical free-text notes are
 * never swept up — the source is hoisted to a single card line and each row
 * keeps only its measure. Everything else shows its note untouched.
 */
export function describeCard(card: ActivityCard): { source: string | null; rows: DescribedRow[] } {
  const parsed = card.events.map((e) => (e.note ? splitNote(e.note) : null));

  const tally = new Map<string, { count: number; sep: boolean }>();
  for (const p of parsed) {
    if (!p) continue;
    const t = tally.get(p.source) ?? { count: 0, sep: false };
    t.count += 1;
    t.sep = t.sep || p.hadSep;
    tally.set(p.source, t);
  }

  let source: string | null = null;
  let best = 1;
  for (const [value, t] of tally) {
    if (t.sep && t.count > best) {
      best = t.count;
      source = value;
    }
  }

  const rows = card.events.map((event, i): DescribedRow => {
    const p = parsed[i];
    if (p && source !== null && p.source === source) {
      return { event, meta: p.measure || null };
    }
    return { event, meta: event.note };
  });

  return { source, rows };
}
